import React, { useCallback, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Row,
  Spin,
  Typography,
  Alert,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { UserState } from "redux-context/user";
import { fetchSupplyMetrics, fetchDemandMetrics, fetchMarketplaceLiquidityMetrics } from "services/SauceService";
import {
  Granularity,
  Comparison,
  SupplyResponse,
  DemandResponse,
  LiquidityResponse,
} from "interfaces/marketplace-dashboard";
import MetricCard from "./components/MetricCard";
import TrendChart from "./components/TrendChart";
import "./MarketplaceDashboard.scss";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const GRANULARITY_OPTIONS: { label: string; value: Granularity }[] = [
  { label: "Monthly", value: "monthly" },
  { label: "Weekly", value: "weekly" },
  { label: "Daily", value: "daily" },
];

// const COMPARISON_OPTIONS: { label: string; value: Comparison }[] = [
//   { label: "None", value: "none" },
//   { label: "Week over Week", value: "wow" },
//   { label: "Month over Month", value: "mom" },
//   { label: "Quarter over Quarter", value: "qoq" },
//   { label: "Year over Year", value: "yoy" },
// ];

const formatRate = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(1)}%`;
};

const formatCount = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

const formatDurationHours = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  if (v < 24) return `${v.toFixed(1)} h`;
  const d = Math.floor(v / 24);
  const h = v - d * 24;
  return `${d}d ${h.toFixed(1)}h`;
};

const formatAppsPerCampaign = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
};

const MarketplaceDashboard: React.FC = () => {
  const { loggedUser } = useSelector((state: UserState) => state.user);

  const [dateRange, setDateRange] = useState<RangeValue>([
    dayjs().subtract(30, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [granularity, setGranularity] = useState<Granularity>("weekly");
  // const [comparison, setComparison] = useState<Comparison>("none");
  const [comparison] = useState<Comparison>("none");

  const [supplyData, setSupplyData] = useState<SupplyResponse | null>(null);
  const [demandData, setDemandData] = useState<DemandResponse | null>(null);
  const [liquidityData, setLiquidityData] = useState<LiquidityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    setError(null);

    const params = {
      start: dateRange[0].toISOString(),
      end: dateRange[1].toISOString(),
      granularity,
      comparison,
    };

    try {
      const [supply, demand, liquidity] = await Promise.all([
        fetchSupplyMetrics(params),
        fetchDemandMetrics(params),
        fetchMarketplaceLiquidityMetrics(params),
      ]);
      setSupplyData(supply);
      setDemandData(demand);
      setLiquidityData(liquidity);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to load dashboard data. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, granularity, comparison]);

  if (!loggedUser?.email?.includes("@mustard.love")) {
    return (
      <div className="marketplace-dashboard">
        <Alert
          type="error"
          message="Access restricted"
          description="This dashboard is only available to internal Mustard users."
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="marketplace-dashboard">
      {/* Header */}
      <div className="marketplace-dashboard__header">
        <Title level={3} style={{ margin: 0 }}>
          Marketplace Analytics
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Supply (Creators) &amp; Demand (Businesses) platform metrics
        </Text>
      </div>

      {/* Controls */}
      <Card className="marketplace-dashboard__controls" bordered={false}>
        <Row gutter={[16, 12]} align="middle">
          <Col>
            <Text strong style={{ fontSize: 13, marginRight: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val as RangeValue)}
              allowClear={false}
              format="MMM D, YYYY"
            />
          </Col>

          <Col>
            <Text strong style={{ fontSize: 13, marginRight: 8 }}>
              Granularity
            </Text>
            <div className="marketplace-dashboard__granularity-toggle">
              {GRANULARITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`granularity-btn${granularity === opt.value ? " active" : ""}`}
                  onClick={() => setGranularity(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Col>

          {/* <Col>
            <Text strong style={{ fontSize: 13, marginRight: 8 }}>
              Compare
            </Text>
            <Select
              value={comparison}
              onChange={setComparison}
              style={{ width: 200 }}
              options={COMPARISON_OPTIONS}
            />
          </Col> */}

          <Col>
            <Button type="primary" onClick={fetchData} loading={loading}>
              Apply
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Error state */}
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Loading state */}
      {loading && (
        <div className="marketplace-dashboard__loading">
          <Spin size="large" tip="Loading metrics…" />
        </div>
      )}

      {/* Content */}
      {!loading && (supplyData || demandData || liquidityData) && (
        <>
          {/* Supply Section */}
          <div className="marketplace-dashboard__section">
            <div className="marketplace-dashboard__section-header">
              <Title level={4} style={{ margin: 0 }}>
                Supply — Creators
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Creator acquisition, activation, and engagement funnel
              </Text>
            </div>

            {supplyData ? (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="First-time Apply Count"
                      description="Creators who submitted their first-ever application in the selected period."
                      summary={supplyData.summary.first_time_apply_count}
                      comparison={comparison}
                      formatValue={formatCount}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Apply Rate"
                      description="Applications submitted divided by creators invited (via digest emails) in the period. Null when no invites were sent."
                      summary={supplyData.summary.apply_rate}
                      comparison={comparison}
                      formatValue={formatRate}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Active Creators"
                      description="Distinct creators who applied at least once between the start and end of the selected range. With a comparison enabled, the baseline uses the same length window shifted back (e.g. WoW, MoM)."
                      summary={supplyData.summary.active_creators}
                      comparison={comparison}
                      formatValue={formatCount}
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  {supplyData.series.first_time_apply_count.length > 0 && (
                    <Col xs={24} md={12}>
                      <Card bordered={false} className="marketplace-dashboard__chart-card">
                        <TrendChart
                          title="First-time Apply Count"
                          series={supplyData.series.first_time_apply_count}
                          comparison={comparison}
                          formatValue={formatCount}
                        />
                      </Card>
                    </Col>
                  )}
                  {supplyData.series.active_creators.length > 0 && (
                    <Col xs={24} md={12}>
                      <Card bordered={false} className="marketplace-dashboard__chart-card">
                        <TrendChart
                          title="Active Creators by period"
                          series={supplyData.series.active_creators}
                          comparison={comparison}
                          formatValue={formatCount}
                        />
                      </Card>
                    </Col>
                  )}
                </Row>
              </>
            ) : (
              <Alert type="warning" message="No supply data available for this range." showIcon />
            )}
          </div>

          <Divider />

          {/* Demand Section */}
          <div className="marketplace-dashboard__section">
            <div className="marketplace-dashboard__section-header">
              <Title level={4} style={{ margin: 0 }}>
                Demand — Businesses
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Business acquisition and campaign activity funnel
              </Text>
            </div>

            {demandData ? (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <MetricCard
                      title="First-time Campaign Created"
                      description="Businesses that created their very first campaign in the selected period."
                      summary={demandData.summary.first_time_campaign_created}
                      comparison={comparison}
                      formatValue={formatCount}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <MetricCard
                      title="Active Businesses"
                      description="Distinct businesses with an active campaign whose first linked collab overlaps the selected start–end range (collab startDate/endDate from the child collab). With a comparison enabled, the baseline uses the same length window shifted back (e.g. WoW, MoM)."
                      summary={demandData.summary.active_businesses}
                      comparison={comparison}
                      formatValue={formatCount}
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  {demandData.series.first_time_campaign_created.length > 0 && (
                    <Col xs={24} md={12}>
                      <Card bordered={false} className="marketplace-dashboard__chart-card">
                        <TrendChart
                          title="First-time Campaign Created"
                          series={demandData.series.first_time_campaign_created}
                          comparison={comparison}
                          formatValue={formatCount}
                        />
                      </Card>
                    </Col>
                  )}
                  {demandData.series.active_businesses.length > 0 && (
                    <Col xs={24} md={12}>
                      <Card bordered={false} className="marketplace-dashboard__chart-card">
                        <TrendChart
                          title="Active Businesses by period"
                          series={demandData.series.active_businesses}
                          comparison={comparison}
                          formatValue={formatCount}
                        />
                      </Card>
                    </Col>
                  )}
                </Row>
              </>
            ) : (
              <Alert type="warning" message="No demand data available for this range." showIcon />
            )}
          </div>

          <Divider />

          <div className="marketplace-dashboard__section">
            <div className="marketplace-dashboard__section-header">
              <Title level={4} style={{ margin: 0 }}>
                Marketplace liquidity / speed
              </Title>
            </div>

            {liquidityData ? (
              <>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                  Campaigns in this period: {formatCount(liquidityData.meta.campaigns_in_period)} created ·{" "}
                  {formatCount(liquidityData.meta.campaigns_with_applications)} with ≥1 application ·{" "}
                  {formatCount(liquidityData.meta.total_applications)} applications total ·{" "}
                  {formatCount(liquidityData.meta.total_hired_creators)} hired bookings
                </Text>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Median time: campaign → first application"
                      description="Median hours between campaign creation and the first creator application (earliest requestedAt on any booking for that campaign)."
                      summary={liquidityData.summary.median_hours_campaign_to_first_apply}
                      comparison={comparison}
                      formatValue={formatDurationHours}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Average time: campaign → first application"
                      description="Mean hours between campaign creation and the first creator application."
                      summary={liquidityData.summary.mean_hours_campaign_to_first_apply}
                      comparison={comparison}
                      formatValue={formatDurationHours}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Applications per campaign"
                      description="Total applications divided by number of campaigns created in the period."
                      summary={liquidityData.summary.applications_per_campaign}
                      comparison={comparison}
                      formatValue={formatAppsPerCampaign}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Median hired creators per campaign"
                      description="Median count of bookings in Complete or PendingContentReview per campaign (all collabs linked by campaignId)."
                      summary={liquidityData.summary.median_hired_creators_per_campaign}
                      comparison={comparison}
                      formatValue={formatAppsPerCampaign}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Average hired creators per campaign"
                      description="Total hired bookings (Complete or PendingContentReview) divided by number of campaigns created in the period."
                      summary={liquidityData.summary.average_hired_creators_per_campaign}
                      comparison={comparison}
                      formatValue={formatAppsPerCampaign}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <MetricCard
                      title="Late submission rate"
                      description="Late submissions divided by total submissions (Complete bookings plus late). Late = WaitingContent or PendingContentReview, no post links, content due (visit date + 5 days) before the snapshot time. Scoped to bookings on collabs linked to campaigns created in the period."
                      summary={liquidityData.summary.late_submission_rate}
                      comparison={comparison}
                      formatValue={formatRate}
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <Alert type="warning" message="No liquidity data available for this range." showIcon />
            )}
          </div>
        </>
      )}

      {/* Empty state (no data loaded yet) */}
      {!loading && !supplyData && !demandData && !liquidityData && !error && (
        <div className="marketplace-dashboard__empty">
          <Text type="secondary">
            Select a date range and click <strong>Apply</strong> to load metrics.
          </Text>
        </div>
      )}
    </div>
  );
};

export default MarketplaceDashboard;
